/**
 * @module DOM
 */

import { joinClasses } from './classes';

/**
 * Creates a new DOM element with the given tag name and attributes. Also
 * appends any children to the newly created element.
 * @param {string} tag The tag name of the element.
 * @param {(null|HTMLElement|string|number|Array)} child The children to append to the new element.
 * @param {Object.<string, string>} attributes The HTML attributes to add to the new element.
 * @example
 * // returns <div class="books"></div>
 * createElement('div', null, { class: 'books' })
 * @example
 * // returns
 * // <div class="books">
 * //   <div class="book">
 * //     The Book Title
 * //   </div>
 * // </div>
 * var book = createElement('div', 'The Book Title', { class: 'book' })
 * createElement('div', book, { class: 'books' })
 * 
 * // Compact
 * createElement('div',
 *   createElement('div', 'The Book Title', { class: 'book' }),
 *   { class: 'books' }
 * )
 */
export function createElement(tag, child, attributes)
{
    const element = document.createElement(tag);

    for (let i in attributes)
    {
        if (!attributes[i])
        {
            continue;
        }

        if (i === 'class')
        {
            attributes[i] = joinClasses(attributes[i]);
        }

        element.setAttribute(i, attributes[i]);
    }

    appendChild(element, child);

    return element;
}

/**
 * Appends the given child (or children if an array is passed), to the given
 * element. If the child is a string or a number, a text node will be appended
 * instead of the usual HTMLElement.
 * @param {HTMLElement} element The element to append the child to.
 * @param {(HTMLElement|string|number|Array)} child The child or children to append to the element.
 * @example
 * // returns <div class="book">The Book Title</div>
 * appendChild(bookElement, 'The Book Title')
 * @example
 * // returns
 * // <div class="books">
 * //   <div class="book">The Book Title</div>
 * // </div>
 * appendChild(booksElement, bookElement)
 * @example
 * // returns
 * // <div class="books">
 * //   <div class="book">Book One</div>
 * //   <div class="book">Book Two</div>
 * // </div>
 * appendChild(booksElement, [
 *   bookElementOne,
 *   bookElementTwo
 * ])
 */
export function appendChild(element, child)
{
    if (Array.isArray(child))
    {
        child.forEach((item) => {
            appendChild(element, item);
        });

        return element;
    }

    if (typeof child === 'string' || typeof child === 'number')
    {
        child = document.createTextNode(child);
    }

    if (child)
    {
        element.appendChild(child);
    }

    return element;
}

/**
 * Wraps the target element with the given wrapper.
 * @param {HTMLElement} element The element to place inside the wrapper.
 * @param {HTMLElement} wrapper The wrapper that will wrap the other element.
 * @example
 * wrapElement(element, wrapper)
 */
export function wrapElement(element, wrapper)
{
    element.parentNode.insertBefore(wrapper, element);

    wrapper.appendChild(element);
}

/**
 * Returns whether or not the given value is a valid HTML element.
 * @param {*} value The value to test.
 * @example
 * // returns true
 * isElement(document.body)
 * @example
 * // returns false
 * isElement('foo')
 */
export function isElement(value)
{
    return value instanceof Element || value instanceof HTMLElement;
}